import React from 'react';

/**
 * VondiLogo — el wordmark de la marca (Manual de Identidad §, BRAND.md §5).
 *
 * Reglas del manual que este componente hace cumplir por construcción:
 *  - Wordmark en minúsculas, Poppins Bold, tracking −1%.
 *  - La "o" es un anillo perfecto y hueco (nunca la letra "o" rellena).
 *  - Tres versiones: `gradiente` (principal, sobre fondos claros), `negro`
 *    (monocromo) y `blanco` (sobre gradiente, foto o fondos oscuros).
 *  - Área de respeto = la altura de la "o" (1x) en los cuatro lados.
 *  - Tamaño mínimo: 28px de ancho el wordmark; 96px con el descriptor.
 *  - El descriptor "paga menos" nunca es más ancho que el wordmark, y va
 *    siempre en minúsculas.
 *
 * Está construido con texto real (no un trazado vectorial) porque el logotipo
 * ES Poppins Bold: así queda nítido a cualquier tamaño, hereda el hinting de la
 * fuente y no hay que mantener un SVG por variante.
 *
 * Prohibido por el manual y por tanto no expuesto como prop: deformar, rotar,
 * cambiar colores fuera de las tres versiones, alterar el interletraje, rellenar
 * la "o", o aplicar sombras y efectos ajenos al sistema.
 */

/* El wordmark mide aproximadamente 2.9 veces el tamaño de fuente.
   De ahí salen los mínimos del manual convertidos a font-size. */
const WORDMARK_WIDTH_RATIO = 2.9;

/*
  Ajuste al render de referencia (11-ago-2026).

  El manual escribe «Poppins Bold, tracking −1%», y así estaba implementado.
  Pero al comparar el logotipo contra el render real de las pantallas de Iván,
  la palabra es visiblemente más pesada y más apretada, y el anillo de la «o»
  bastante más grande y de trazo mucho más fino. Ese contraste —letras gruesas
  contra un anillo delgado— es justo lo que le da carácter.

  Manda el render, que es el parámetro visual acordado. Los números salen de
  medir el propio archivo: anillo ≈ 0.60em de diámetro con trazo ≈ 0.065em,
  contra letras en 800.
*/
const PESO_WORDMARK = 800;
const ANILLO_DIAMETRO = 0.6;   // en em
const ANILLO_TRAZO = 0.065;    // en em
const TRACKING = '-0.03em';
const MIN_WIDTH_PX = 28;
const MIN_WIDTH_WITH_DESCRIPTOR_PX = 96;

/* Relleno de cada variante. El gradiente es el de marca a 100° (BRAND.md §1). */
const FILLS = {
  gradiente: 'linear-gradient(100deg, #FA4D0A, #F73E48)',
  negro: 'linear-gradient(100deg, #0E0E10, #0E0E10)',
  blanco: 'linear-gradient(100deg, #FFFFFF, #FFFFFF)',
};

export function VondiLogo({
  variant = 'gradiente',
  /** Tamaño de fuente en px del wordmark. */
  size = 24,
  /** Muestra el descriptor "paga menos" bajo el wordmark. */
  withDescriptor = false,
  /** Reserva el área de respeto del manual (1x la altura de la "o") alrededor. */
  respectArea = false,
  className = '',
  style = {},
  ...rest
}) {
  const fill = FILLS[variant] || FILLS.gradiente;

  /* Se respeta el tamaño mínimo del manual en vez de confiar en quien lo llama. */
  const minSize =
    (withDescriptor ? MIN_WIDTH_WITH_DESCRIPTOR_PX : MIN_WIDTH_PX) / WORDMARK_WIDTH_RATIO;
  const fontSize = Math.max(size, minSize);

  if (process.env.NODE_ENV !== 'production' && size < minSize) {
    const pedido = Math.round(size * WORDMARK_WIDTH_RATIO);
    const minimo = withDescriptor ? MIN_WIDTH_WITH_DESCRIPTOR_PX : MIN_WIDTH_PX;
    // eslint-disable-next-line no-console
    console.warn(
      `[VondiLogo] Tamaño por debajo del mínimo del manual: ~${pedido}px de ancho ` +
        `(mínimo ${minimo}px${withDescriptor ? ' con descriptor' : ''}). Se forzó al mínimo.`
    );
  }

  /* El anillo de la "o": diámetro a la altura de x, trazo fino, hueco.
     Va con máscara radial para que el degradado también lo atraviese —
     un `border` no admite gradiente. */
  const ringSize = fontSize * ANILLO_DIAMETRO;
  const ringStroke = Math.max(1, fontSize * ANILLO_TRAZO);
  /*
    `closest-side` no es un detalle: sin él, un `radial-gradient(circle, …)`
    mide su 100% hasta la **esquina** de la caja, no hasta el borde. Como la
    caja es cuadrada, esa esquina está a 1.41× el radio, así que el hueco salía
    un 30% más chico de lo calculado y el anillo se veía casi relleno —tres
    veces más grueso de lo que dice el manual—. Con `closest-side`, el 100%
    coincide con el radio y la resta es exacta.
  */
  const ringHole = `calc(100% - ${ringStroke}px)`;

  const ring = (
    <span
      aria-hidden="true"
      style={{
        display: 'inline-block',
        width: ringSize,
        height: ringSize,
        margin: `0 ${fontSize * 0.012}px`,
        backgroundImage: fill,
        WebkitMaskImage: `radial-gradient(circle closest-side, transparent ${ringHole}, #000 ${ringHole})`,
        maskImage: `radial-gradient(circle closest-side, transparent ${ringHole}, #000 ${ringHole})`,
        borderRadius: '50%',
        flex: 'none',
      }}
    />
  );

  const textStyle = {
    backgroundImage: fill,
    WebkitBackgroundClip: 'text',
    backgroundClip: 'text',
    color: 'transparent',
    fontFamily: "'Poppins', 'SF Pro Display', 'Segoe UI', Roboto, system-ui, sans-serif",
    fontWeight: PESO_WORDMARK,
    fontSize,
    letterSpacing: TRACKING,
    lineHeight: 1,
  };

  return (
    <span
      className={className}
      /* Un solo nodo accesible: el resto es decorativo. */
      role="img"
      aria-label={withDescriptor ? 'vondi — paga menos' : 'vondi'}
      style={{
        display: 'inline-flex',
        flexDirection: 'column',
        alignItems: 'center',
        padding: respectArea ? ringSize : 0,
        ...style,
      }}
      {...rest}
    >
      <span
        aria-hidden="true"
        style={{ display: 'inline-flex', alignItems: 'baseline', lineHeight: 1 }}
      >
        <span style={textStyle}>v</span>
        {/* El anillo se alinea al centro de la altura de x, no a la línea base. */}
        <span
          aria-hidden="true"
          style={{ display: 'inline-flex', alignItems: 'center', height: fontSize * 0.7 }}
        >
          {ring}
        </span>
        <span style={textStyle}>ndi</span>
      </span>

      {withDescriptor && (
        <span
          aria-hidden="true"
          style={{
            ...textStyle,
            /* Nunca más ancho que el wordmark: el descriptor va a ~0.28 del
               tamaño del logotipo, que en "paga menos" queda holgadamente dentro. */
            fontSize: fontSize * 0.235,
            fontWeight: 500,
            letterSpacing: '0.04em',
            marginTop: fontSize * 0.2,
          }}
        >
          paga menos
        </span>
      )}
    </span>
  );
}

export default VondiLogo;
